/**
 * ReportPreviewPanel — renderiza o `document` model do relatório gerado.
 *
 * Antes de gerar: placeholder neutro (descrição do tipo selecionado). Depois:
 * cabeçalho real (dados do tenant logado, não o "NetComet Soluções" fake do
 * mockup), seções key/value, painel de hash de integridade e ações de download
 * (JSON + PDF via print). NÃO renderiza assinatura quando `signed=false` (P-4).
 */

import { HashIntegrityPanel } from './HashIntegrityPanel';
import { downloadReportJson } from './report-download';

import type { GeneratedLgpdReport, LgpdReportTypeDescriptor, ReportDocumentSection } from './types';

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return JSON.stringify(value);
}

function SectionBlock({ section }: { section: ReportDocumentSection }): JSX.Element {
  return (
    <div className="rep-doc-section">
      <h4 className="rep-doc-section__heading">{section.heading}</h4>
      {section.items.length === 0 ? (
        <p className="rep-doc-section__empty">Sem registros no período/escopo.</p>
      ) : (
        section.items.map((item, idx) => (
          // Projeção read-only determinística do relatório: ordem estável, sem id próprio.
          <dl className="rep-doc-kv" key={`${section.heading}#${idx}`}>
            {Object.entries(item).map(([k, v]) => (
              <div className="rep-doc-kv__row" key={k}>
                <dt className="rep-doc-kv__key">{k}</dt>
                <dd className="rep-doc-kv__val">{formatValue(v)}</dd>
              </div>
            ))}
          </dl>
        ))
      )}
    </div>
  );
}

export function ReportPreviewPanel({
  selectedDescriptor,
  report,
  isGenerating,
  error,
}: {
  selectedDescriptor: LgpdReportTypeDescriptor | undefined;
  report: GeneratedLgpdReport | null;
  isGenerating: boolean;
  error: string | null;
}): JSX.Element {
  return (
    <section className="rep-preview" aria-label="Preview do relatório">
      <header className="rep-preview__head">
        <span className="rep-preview__title">
          Preview · {selectedDescriptor?.label ?? 'Relatório'}
        </span>
      </header>

      <div className="rep-preview__body">
        {error !== null && (
          <div className="rep-alert rep-alert--error" role="alert">
            {error}
          </div>
        )}

        {report === null ? (
          <div className="rep-preview__placeholder">
            {isGenerating ? (
              <p>Gerando relatório…</p>
            ) : (
              <>
                <p className="rep-preview__placeholder-title">Pronto para gerar</p>
                <p className="rep-preview__placeholder-sub">
                  {selectedDescriptor?.hint ??
                    'Selecione um tipo de relatório, configure o escopo e clique em Gerar relatório.'}
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="rep-printable">
            <div className="rep-doc-header">
              <h3 className="rep-doc-header__title">{report.document.title}</h3>
              <dl className="rep-doc-kv">
                {Object.entries(report.document.header).map(([k, v]) => (
                  <div className="rep-doc-kv__row" key={k}>
                    <dt className="rep-doc-kv__key">{k}</dt>
                    <dd className="rep-doc-kv__val">{formatValue(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {report.document.sections.map((section) => (
              <SectionBlock key={section.heading} section={section} />
            ))}

            <HashIntegrityPanel
              algorithm={report.integrity.algorithm}
              hash={report.integrity.hash}
              signed={report.integrity.signed}
            />
          </div>
        )}

        {report !== null && (
          <div className="rep-actions">
            <button
              type="button"
              className="rep-btn"
              onClick={() => {
                downloadReportJson(report);
              }}
            >
              Baixar JSON
            </button>
            <button
              type="button"
              className="rep-btn"
              onClick={() => {
                if (typeof window !== 'undefined') window.print();
              }}
            >
              Baixar PDF
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
